'use client';

import { fetchProductReviews } from '@/api/product';
import { IReviewsData, IReviewsItem } from '@/api/types/product';
import { IProductDetail } from '@/api/types/public';
import Button from '@/components/Button';
import InfiniteScroll from '@/components/InfiniteScroll';
import Pagination from '@/components/Pagination';
import SortMenuWithPopper, { SortOption } from '@/components/SortMenuWithPopper';

import StarRating from '@/components/StarRating';
import { useFirstEnter } from '@/hooks/useFirstEnter';
import { colors, font } from '@/theme/zeelool.theme';
import logger from '@/utils/client-logger';
import { getDisplayStars, numberUnitFormat, scrollToAnchor } from '@/utils/tools';
import { Box, LinearProgress, Tab, Tabs } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useTranslations } from 'next-intl';
import { useCallback, useEffect, useMemo, useState } from 'react';
import CustomerReviewsItem, {
  IProductDetailsPageInteractionBtnClickParams,
  ReviewItemData,
} from './CustomerReviewsItem';
import CustomerReviewsSkeleton from './CustomerReviewsSkeleton';
import { formatNumber, getIntlLocale } from '@/utils/tools';
import { env } from '@/utils/env';
import { useMergedReviewList } from '@/app/goods-detail/hooks/useReviewListHelpful';

// 自定义样式组件
const PCContainer = styled(Box)({
  margin: '36px 2.5%',
});

const PCTitle = styled('p')({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  fontFamily: font.bb,
  fontWeight: 'normal',
  fontSize: '32px',
  color: colors.primaryBlack,
  lineHeight: '39px',
  textAlign: 'center',
  marginBottom: '32px',
  '& span': {
    fontSize: '20px',
    fontFamily: font.br,
  },
});

const PCStarContainer = styled(Box)({
  background: '#F7F7F7',
  padding: '33px 0',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'flex-start',
  marginTop: '32px',
  marginBottom: '32px',
});

const PCReviewLabels = styled(Box)({
  display: 'flex',
  justifyContent: 'flex-start',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: '16px',
  padding: '32px 13%',
});

const PCReviewList = styled(Box)({
  padding: '0 13%',
  position: 'relative',
});

const CustomTabs = styled(Tabs)({
  '& .MuiTabs-indicator': {
    backgroundColor: colors.primaryBlack,
  },
  '& .MuiTab-root': {
    fontFamily: font.br,
    fontWeight: 'normal',
    fontSize: '16px',
    color: colors.primaryBlack,
    lineHeight: '40px',
    textAlign: 'left',
    textTransform: 'none',
    padding: '0',
    '&.Mui-selected': {
      fontFamily: font.bb,
      fontWeight: 'normal',
      fontSize: '16px',
      color: colors.primaryBlack,
      lineHeight: '19px',
    },
  },
});

interface CustomerReviewsListProps {
  product: IProductDetail;
  reviewsData: IReviewsData;
  partialReviewList: Array<Partial<IReviewsItem>>;
  isGlass: boolean;
  isWeb?: boolean;
  SDProductDetailsPageInteractionBtnClick?: (
    params: IProductDetailsPageInteractionBtnClickParams,
  ) => void;
}

export const WEB_SSR_REVIEW_SIZE_LIMIT = 5;

const CustomerReviewsList: React.FC<CustomerReviewsListProps> = ({
  product,
  reviewsData,
  partialReviewList,
  isGlass,
  isWeb = false,
  SDProductDetailsPageInteractionBtnClick,
}) => {
  const locale = getIntlLocale(env.NEXT_PUBLIC_SITE_NAME);
  const t = useTranslations('goodsDetail.customerReivewsList');
  const [page, setPage] = useState(1);
  // PC 端先从服务端拿一下数据，做 SSR
  const [_reviewsList, setReviewsList] = useState<ReviewItemData[]>(
    (isWeb ? reviewsData?.list?.slice(0, WEB_SSR_REVIEW_SIZE_LIMIT) : reviewsData?.list) || [],
  );
  const reviewsList = useMergedReviewList(_reviewsList, partialReviewList);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [checkedTags, setCheckedTags] = useState<string[]>([]);
  const [currentReviewsData, setCurrentReviewsData] = useState<IReviewsData>(reviewsData);
  const [tabName, setTabName] = useState<'all' | 'image'>('all');
  const [sortValue, setSortValue] = useState<string | number>(1);
  const [total, setTotal] = useState(reviewsData.current_count);
  const firstEnterRef = useFirstEnter();

  const limit = isWeb ? 5 : 10;

  const sortOptions: SortOption[] = [
    { label: t('highestToLowestRating'), value: 1 },
    { label: t('lowestToHighestRating'), value: 3 },
    { label: t('mostRecent'), value: 2 },
  ];

  const appraiseLabels = useMemo(() => {
    const labels: Array<{ label: string; value: number; labelShow: string }> = [];
    const { appraise_label = {} } = currentReviewsData;
    if (appraise_label && typeof appraise_label === 'object') {
      Object.entries(appraise_label).forEach(([key, value]) => {
        const numValue = typeof value === 'string' ? parseInt(value, 10) : value;
        const obj = {
          fast_shipping: t('sortOptions.fast_shipping'),
          quality: t('sortOptions.quality'),
          prescription: t('sortOptions.prescription'),
          design: t('sortOptions.design'),
          price: t('sortOptions.price'),
          size: t('sortOptions.size'),
          single_vision: t('sortOptions.single_vision'),
          progressive: t('sortOptions.progressive'),
          reading_glasses: t('sortOptions.reading_glasses'),
          sunglasses_prescription: t('sortOptions.sunglasses_prescription'),
          images: t('sortOptions.images'),
        };

        if (key !== 'images' && typeof numValue === 'number' && !isNaN(numValue)) {
          let labelShow = '';
          try {
            labelShow = obj[key];
          } catch (error) {
            logger.log(error);
            labelShow = key;
          }
          labels.push({
            label: key,
            value: numValue,
            labelShow,
          });
        }
      });
    }
    return labels;
  }, [currentReviewsData]);

  const getPercentage = useCallback((number: number) => {
    try {
      const percent = (number / 5) * 100;
      return isNaN(percent) ? 0 : Math.min(percent, 100);
    } catch (e) {
      logger.log(e);
      return 0;
    }
  }, []);

  const detailDistributed = useMemo(() => {
    const _arr = currentReviewsData?.detail_distributed || [];
    const arr = [..._arr];
    return arr.reverse();
  }, [currentReviewsData]);

  const getProductReviews = useCallback(
    async (resetList = false) => {
      const image = tabName === 'image' ? 'with_image' : '';
      const appraise_label = checkedTags.join(',');

      try {
        setReviewLoading(true);
        const params = {
          product_id: product.id.toString(),
          page: page.toString(),
          limit: limit.toString(),
          appraise_label,
          image,
          sort: Number(sortValue),
        };

        const response = await fetchProductReviews(params);
        if (response.code === 200 && response.data) {
          const newList = response.data.list || [];
          if (resetList || isWeb) {
            setReviewsList(newList);
          } else {
            setReviewsList((prev) => [...prev, ...newList]);
          }
          setCurrentReviewsData(response.data);
          setTotal(response.data.current_count || 0);
          return newList;
        }
      } catch (error) {
        logger.log(error);
      } finally {
        setReviewLoading(false);
      }
      return [];
    },
    [product.id, page, limit, tabName, checkedTags, sortValue, isWeb],
  );

  const checkTag = ({ label, labelShow }: { label: string; labelShow: string }) => {
    const tag = label;
    setReviewLoading(true);
    setCheckedTags((prev) => {
      const newTags = [...prev];
      const labelShowFormatted = labelShow.charAt(0).toUpperCase() + labelShow.slice(1);
      if (newTags.includes(tag)) {
        SDProductDetailsPageInteractionBtnClick({
          page_name: '商品详情评论列表',
          interaction_type: '筛选评论',
          interaction_name: '取消选中分类',
          tab_name: tabName === 'all' ? 'All' : 'Images',
          comment_category: labelShowFormatted,
        });
        newTags.splice(newTags.indexOf(tag), 1);
      } else {
        SDProductDetailsPageInteractionBtnClick({
          page_name: '商品详情评论列表',
          interaction_type: '筛选评论',
          interaction_name: '选中分类',
          tab_name: tabName === 'all' ? 'All' : 'Images',
          comment_category: labelShowFormatted,
        });
        newTags.push(tag);
      }
      return newTags;
    });
    setPage(1);
    setReviewsList([]);
  };

  const tabChange = useCallback((tab: 'all' | 'image') => {
    let interaction_name = '';
    if (tab === 'all') {
      interaction_name = 'tab切换';
    } else if (checkedTags.includes('image')) {
      interaction_name = '取消分类';
    } else {
      interaction_name = 'tab切换';
    }
    SDProductDetailsPageInteractionBtnClick({
      page_name: '商品详情评论列表',
      interaction_type: '筛选评论',
      interaction_name,
      tab_name: tab === 'all' ? 'All' : 'Images',
    });
    setReviewLoading(true);
    setTabName(tab);
    setPage(1);
    setReviewsList([]);
  }, []);

  const handleSortChange = useCallback((value: string | number) => {
    setReviewLoading(true);
    setSortValue(value);
    setPage(1);
    setReviewsList([]);
  }, []);

  const handlePageChange = useCallback((event: React.ChangeEvent<unknown>, newPage: number) => {
    setPage(newPage);
    scrollToAnchor('.customer-reviews-anchor');
  }, []);

  const handleReachBottom = useCallback(() => {
    if (reviewsList.length < total) {
      setPage((prev) => prev + 1);
    }
  }, [reviewsList.length, total]);

  const resetList = useCallback(() => {
    setReviewLoading(true);
    setTabName('all');
    setCheckedTags([]);
    setPage(1);
    setSortValue(1);
    setReviewsList([]);
  }, []);

  useEffect(() => {
    if (firstEnterRef.current) return;
    setCurrentReviewsData(reviewsData);
    resetList();
  }, [reviewsData, resetList]);

  useEffect(() => {
    if (firstEnterRef.current) return;
    if (page === 1) {
      getProductReviews(true);
    } else if (page > 1) {
      getProductReviews(false);
    }
  }, [page, tabName, checkedTags, sortValue, getProductReviews]);

  if (isWeb) {
    return (
      <PCContainer id="customer-reviews-list">
        <PCTitle>
          {t('reviews')}
          {currentReviewsData.count > 0 && (
            <span className="notranslate">({numberUnitFormat(currentReviewsData.count)})</span>
          )}
        </PCTitle>

        <PCStarContainer>
          <Box className="flex flex-col items-start min-w-[130px] w-[150px] mr-[40px]">
            <span className="text-[3.33vw] font-bb leading-[3.33vw] mb-16">
              {formatNumber(reviewsData.stars || 5, 1, locale)}
            </span>
            <StarRating
              value={getDisplayStars(currentReviewsData.stars)}
              precision={0.5}
              readOnly
              size="small"
              iconSize="16px"
              gap="6px"
            />
          </Box>

          <Box className="flex flex-col justify-between w-[336px] min-w-[336px]">
            {detailDistributed.map((item: any, index: number) => (
              <Box
                key={index}
                className="flex items-center justify-between w-[336px] min-w-[336px] mb-[10px] last:mb-0"
              >
                <span className="text-[14px] min-w-[50px] whitespace-nowrap">
                  {item.star} {item.star === 1 ? t('star') : t('stars')}
                </span>
                <LinearProgress
                  variant="determinate"
                  value={item.percent}
                  className="ml-[10px] w-[236px]"
                  sx={{
                    height: 6,
                    borderRadius: 3,
                    backgroundColor: '#E0E0E0',
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: colors.primaryBlack,
                      borderRadius: 3,
                    },
                  }}
                />
                <span className="inline-block w-[30px] text-[14px] text-left">{item.percent}%</span>
              </Box>
            ))}
          </Box>

          <Box className="flex flex-col ml-[15.625vw]">
            <p className="text-[20px] font-bb leading-[24px] mb-[32px] whitespace-nowrap">
              {t('averageCustomerRatings')}
            </p>
            <Box className="flex flex-col justify-between">
              {currentReviewsData.stars_detail && (
                <>
                  {currentReviewsData.stars_detail.quality !== undefined &&
                    currentReviewsData.stars_detail.quality !== null && (
                      <Box className="flex items-center justify-between w-[257px] min-w-[257px] mb-[10px]">
                        <span className="text-[14px] w-[80px] leading-[17px]">{t('quality')}</span>
                        <LinearProgress
                          variant="determinate"
                          value={getPercentage(Number(currentReviewsData.stars_detail.quality))}
                          className="w-[165px] mx-[5px]"
                          sx={{
                            height: 6,
                            borderRadius: 3,
                            backgroundColor: '#E0E0E0',
                            '& .MuiLinearProgress-bar': {
                              backgroundColor: colors.primaryGreen,
                              borderRadius: 3,
                            },
                          }}
                        />
                        <span className="text-[14px] font-bb">
                          {formatNumber(currentReviewsData.stars_detail.quality, 1, locale)}
                        </span>
                      </Box>
                    )}

                  {currentReviewsData.stars_detail.fit !== undefined &&
                    currentReviewsData.stars_detail.fit !== null && (
                      <Box className="flex items-center justify-between w-[257px] min-w-[257px] mb-[10px]">
                        <span className="text-[14px] w-[80px] leading-[17px]">{t('fit')}</span>
                        <LinearProgress
                          variant="determinate"
                          value={getPercentage(Number(currentReviewsData.stars_detail.fit))}
                          className="w-[165px] mx-[5px]"
                          sx={{
                            height: 6,
                            borderRadius: 3,
                            backgroundColor: '#E0E0E0',
                            '& .MuiLinearProgress-bar': {
                              backgroundColor: colors.primaryGreen,
                              borderRadius: 3,
                            },
                          }}
                        />
                        <span className="text-[14px] font-bb">
                          {formatNumber(currentReviewsData.stars_detail.fit, 1, locale)}
                        </span>
                      </Box>
                    )}

                  {currentReviewsData.stars_detail.design !== undefined &&
                    currentReviewsData.stars_detail.design !== null && (
                      <Box className="flex items-center justify-between w-[257px] min-w-[257px]">
                        <span className="text-[14px] w-[80px] leading-[17px]">{t('design')}</span>
                        <LinearProgress
                          variant="determinate"
                          value={getPercentage(Number(currentReviewsData.stars_detail.design))}
                          className="w-[165px] mx-[5px]"
                          sx={{
                            height: 6,
                            borderRadius: 3,
                            backgroundColor: '#E0E0E0',
                            '& .MuiLinearProgress-bar': {
                              backgroundColor: colors.primaryGreen,
                              borderRadius: 3,
                            },
                          }}
                        />
                        <span className="text-[14px] font-bb">
                          {formatNumber(currentReviewsData.stars_detail.design, 1, locale)}
                        </span>
                      </Box>
                    )}
                </>
              )}
            </Box>
          </Box>
        </PCStarContainer>
        <Box className="customer-reviews-anchor" />
        <PCReviewLabels>
          {appraiseLabels.map(
            (labelItem) =>
              labelItem.value > 0 && (
                <Box
                  key={labelItem.label}
                  onClick={() => {
                    checkTag({
                      label: labelItem.label,
                      labelShow: labelItem.labelShow,
                    });
                  }}
                  className={`cursor-pointer list-none rounded-[20px] border  px-[18px] py-[8px] text-[14px] transition-all duration-300 ${
                    checkedTags.includes(labelItem.label)
                      ? 'bg-[#282828] text-white border-[#282828]'
                      : 'hover:bg-gray-50 bg-white border-[#D4D4D4]'
                  }`}
                >
                  <span>{labelItem.labelShow}</span>{' '}
                  <span>({labelItem.value > 999 ? '999+' : labelItem.value})</span>
                </Box>
              ),
          )}
        </PCReviewLabels>

        <PCReviewList>
          <div className="relative">
            <CustomTabs
              value={tabName === 'all' ? 0 : 1}
              onChange={(event, newValue) => tabChange(newValue === 0 ? 'all' : 'image')}
              className="border-b border-[#eeeeee]"
            >
              <Tab
                label={
                  <span className="notranslate">
                    {t('all')}({numberUnitFormat(currentReviewsData.all_count || 0)})
                  </span>
                }
              />
              {/* 跟老站保持一致，Images 这里不转成 xK 形式 */}
              <Tab
                label={
                  <span className="notranslate">
                    {t('images')}({currentReviewsData.image_count || 0})
                  </span>
                }
              />
            </CustomTabs>

            <Box className="absolute right-0 top-1/2 -translate-y-1/2">
              <SortMenuWithPopper
                options={sortOptions}
                value={sortValue}
                onChange={handleSortChange}
                showColon
                menuListProps={{
                  sx: {
                    padding: 0,
                  },
                }}
                placement="bottom-end"
              />
            </Box>
          </div>

          <Box className="mt-[15px]">
            {reviewLoading && reviewsList.length === 0 ? (
              <CustomerReviewsSkeleton isWeb count={limit} />
            ) : (
              reviewsList.map((item, index) => (
                <Box key={item.id || index}>
                  <CustomerReviewsItem
                    itemData={item}
                    isGlass={isGlass}
                    inList
                    isWeb
                    tabName={tabName}
                    SDProductDetailsPageInteractionBtnClick={
                      SDProductDetailsPageInteractionBtnClick
                    }
                  />
                </Box>
              ))
            )}
          </Box>
        </PCReviewList>

        {reviewsData.count > limit && (
          <Box className="flex justify-center mt-[32px]">
            <Pagination current={page} total={total} pageSize={limit} onChange={handlePageChange} />
          </Box>
        )}
      </PCContainer>
    );
  }

  return (
    <Box className="min-h-screen bg-[#F4F4F4]">
      <Box className="mx-auto bg-white rounded-[10px] p-[25px] mb-[16px] flex justify-between items-center">
        <Box className="flex flex-col items-center mr-[32px]">
          <span className="text-[36px] text-primary-black font-bb leading-[43px] mb-[5px]">
            {formatNumber(currentReviewsData.stars || 0, 1, locale)}
          </span>
          <StarRating
            value={getDisplayStars(currentReviewsData.stars)}
            precision={0.5}
            readOnly
            size="small"
            iconSize="14px"
            gap="6px"
          />
        </Box>

        <Box className="flex-1">
          {currentReviewsData.stars_detail && (
            <>
              {currentReviewsData.stars_detail.quality !== undefined &&
                currentReviewsData.stars_detail.quality !== null && (
                  <Box className="flex items-center text-primary-black h-[14px] mb-[10px] w-[186px]">
                    <span className="text-[14px] min-w-[52px] pr-[5px]">{t('quality')}</span>
                    <span className="font-bb text-[12px]">
                      {Number(currentReviewsData.stars_detail.quality).toFixed(1)}
                    </span>
                    <LinearProgress
                      variant="determinate"
                      value={getPercentage(Number(currentReviewsData.stars_detail.quality))}
                      className="ml-[12px] flex-1"
                      sx={{
                        height: 6,
                        borderRadius: 3,
                        backgroundColor: '#E0E0E0',
                        '& .MuiLinearProgress-bar': {
                          backgroundColor: colors.primaryGreen,
                          borderRadius: 3,
                        },
                      }}
                    />
                  </Box>
                )}

              {currentReviewsData.stars_detail.fit !== undefined &&
                currentReviewsData.stars_detail.fit !== null && (
                  <Box className="flex items-center text-primary-black h-[14px] mb-[10px] w-[186px]">
                    <span className="text-[14px] min-w-[52px] pr-[5px]">{t('fit')}</span>
                    <span className="font-bb text-[12px]">
                      {Number(currentReviewsData.stars_detail.fit).toFixed(1)}
                    </span>
                    <LinearProgress
                      variant="determinate"
                      value={getPercentage(Number(currentReviewsData.stars_detail.fit))}
                      className="ml-[12px] flex-1"
                      sx={{
                        height: 6,
                        borderRadius: 3,
                        backgroundColor: '#E0E0E0',
                        '& .MuiLinearProgress-bar': {
                          backgroundColor: colors.primaryGreen,
                          borderRadius: 3,
                        },
                      }}
                    />
                  </Box>
                )}

              {currentReviewsData.stars_detail.design !== undefined &&
                currentReviewsData.stars_detail.design !== null && (
                  <Box className="flex items-center text-[#282828] h-[14px] w-[186px]">
                    <span className="text-[14px] min-w-[52px] pr-[5px]">{t('design')}</span>
                    <span className="font-bb text-[12px]">
                      {Number(currentReviewsData.stars_detail.design).toFixed(1)}
                    </span>
                    <LinearProgress
                      variant="determinate"
                      value={getPercentage(Number(currentReviewsData.stars_detail.design))}
                      className="ml-[12px] flex-1"
                      sx={{
                        height: 6,
                        borderRadius: 3,
                        backgroundColor: '#E0E0E0',
                        '& .MuiLinearProgress-bar': {
                          backgroundColor: colors.primaryGreen,
                          borderRadius: 3,
                        },
                      }}
                    />
                  </Box>
                )}
            </>
          )}
        </Box>
      </Box>

      <Box className="flex flex-wrap gap-[8px]">
        {appraiseLabels.map(
          (labelItem) =>
            labelItem.value > 0 && (
              <Button
                key={labelItem.label}
                onClick={() =>
                  checkTag({
                    label: labelItem.label,
                    labelShow: labelItem.labelShow,
                  })
                }
                sx={{
                  padding: '0 10px',
                  height: '24px',
                  borderRadius: '14px',
                  fontSize: '11px',
                  transition: 'all 0.3s ease',
                  backgroundColor: checkedTags.includes(labelItem.label)
                    ? colors.primaryBlack
                    : 'white',
                  color: checkedTags.includes(labelItem.label) ? 'white' : colors.primaryBlack,
                }}
              >
                <span>{labelItem.labelShow}</span>{' '}
                <span>({numberUnitFormat(labelItem.value)})</span>
              </Button>
            ),
        )}
      </Box>

      <Box className="flex pt-[22px] pb-[10px] bg-[#F4F4F4]">
        <div
          onClick={() => tabChange('all')}
          className={`text-primary-black text-[12px] leading-[17px] border-b-2 flex-shrink-0 mr-[28px] ${
            tabName === 'all'
              ? 'font-bb text-[14px] pb-[4px] border-primary-black'
              : 'border-transparent'
          }`}
        >
          <span>{t('all')}</span>{' '}
          <span>({numberUnitFormat(currentReviewsData.all_count || 0)})</span>
        </div>

        <div
          onClick={() => tabChange('image')}
          className={`text-primary-black text-[12px] leading-[17px] border-b-2 flex-shrink-0 ${
            tabName === 'image'
              ? 'font-bb text-[14px] pb-[4px] border-primary-black'
              : 'border-transparent'
          }`}
        >
          <span>{t('images')}</span>{' '}
          <span>({numberUnitFormat(currentReviewsData.image_count || 0)})</span>
        </div>

        <Box className="ml-auto">
          {/* 虽然只变更了 PC端，但是统一一个组件吧 */}
          <SortMenuWithPopper
            options={sortOptions}
            value={sortValue}
            onChange={handleSortChange}
            placement="bottom-end"
            buttonStyle={{
              fontSize: '13px',
              color: colors.primaryBlack,
              padding: 0,
              minWidth: 'auto',
              transform: 'translateY(-4px)',
              fontFamily: font.br,
            }}
          />
        </Box>
      </Box>

      <Box className="mx-auto">
        {reviewLoading && reviewsList.length === 0 ? (
          <CustomerReviewsSkeleton count={limit} />
        ) : (
          reviewsList.map((item, index) => (
            <Box key={item.id || index} className="mb-[8px]">
              <CustomerReviewsItem
                itemData={item}
                isGlass={isGlass}
                inList
                SDProductDetailsPageInteractionBtnClick={SDProductDetailsPageInteractionBtnClick}
                tabName={tabName}
              />
            </Box>
          ))
        )}
      </Box>
      <InfiniteScroll
        onReachBottom={handleReachBottom}
        loading={reviewLoading}
        page={page}
        total={total}
        limit={limit}
      />
      <Box className="h-[100px]" />
    </Box>
  );
};

export default CustomerReviewsList;
